"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { Container } from "@/components/common/Container";
import { FounderTrustPanel } from "@/components/common/FounderTrustPanel";
import { Layout } from "@/components/layout/Layout";
import { projects } from "@/content/projects";
import { ROUTES } from "@/config/routes";

export default function GalleryPage() {
  return (
    <Layout title="Swargaseema Gallery">
      <main>
        <PageHero eyebrow="Gallery" title="Project visuals grouped by community." copy="Inspect each project through real site photos, amenity shots, roads, entries, layouts, and approval visuals." />
        <section className="bg-[var(--cream)] py-10 lg:py-14">
          <Container>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
            >
              <FounderTrustPanel
                compact
                image="/brand/founder-trust-tan.webp"
                title="Real project visuals, reviewed with founder-led clarity."
                copy="Use the gallery to compare what is on the ground, then speak with the team for route planning, availability, and documentation context."
              />
            </motion.div>
          </Container>
        </section>
        {projects.map((project, projectIndex) => (
          <section key={project.slug} className={`py-16 lg:py-24 ${projectIndex % 2 === 1 ? "bg-[var(--cream)]" : ""}`}>
            <Container>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6 }}
                className="mb-8 grid items-center gap-5 lg:grid-cols-[190px_minmax(0,1fr)_auto]"
              >
                <div className="relative h-24 w-48 drop-shadow-[0_12px_18px_rgba(23,60,49,0.18)]">
                  <Image src={project.logo} alt={`${project.name} logo`} fill sizes="190px" className="object-contain object-left" />
                </div>
                <div className="min-w-0">
                  <p className="mb-2 text-xs font-black uppercase tracking-[0.14em] text-[var(--gold)]">{project.location}</p>
                  <h2 className="break-words font-serif text-3xl font-medium text-[var(--ink)] sm:text-4xl">{project.name}</h2>
                </div>
                <Link href={`${ROUTES.PROJECTS}/${project.slug}`} className="inline-flex items-center gap-2 text-sm font-black text-[var(--leaf)] transition hover:text-[var(--ink)]">
                  View project <ArrowRight size={16} />
                </Link>
              </motion.div>
              <div className="grid gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {project.galleryImages.slice(0, 6).map(([src, alt], index) => (
                  <motion.figure
                    key={src}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`group relative overflow-hidden rounded-xl sm:rounded-2xl bg-[var(--ink)] shadow-sm transition-all hover:shadow-md ${index === 0
                        ? "aspect-video sm:col-span-2 lg:row-span-2 lg:aspect-auto"
                        : "aspect-[4/3] sm:aspect-[1.5] lg:aspect-[4/3]"
                      }`}
                  >
                    <Image
                      src={src}
                      alt={alt}
                      fill
                      sizes={index === 0 ? "(max-width: 1024px) 100vw, 66vw" : "(max-width: 800px) 50vw, 33vw"}
                      className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
                    <figcaption className="absolute inset-x-4 bottom-4 sm:inset-x-6 sm:bottom-5 text-xs sm:text-sm font-bold tracking-wide text-white drop-shadow-md">
                      {alt}
                    </figcaption>
                  </motion.figure>
                ))}
              </div>
            </Container>
          </section>
        ))}
      </main>
    </Layout>
  );
}

function PageHero({ eyebrow, title, copy }: { eyebrow: string; title: string; copy: string }) {
  return (
    <section className="bg-[var(--ink)] pb-20 pt-32 text-[var(--white)] lg:pt-40">
      <Container>
        <p className="mb-3 text-xs font-black uppercase tracking-[0.14em] text-[var(--gold-strong)]">{eyebrow}</p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-4xl break-words font-serif text-3xl font-medium leading-tight sm:text-5xl lg:text-6xl sm:leading-none"
        >
          {title}
        </motion.h1>
        <p className="mt-5 max-w-2xl text-base leading-8 text-white/75 sm:text-lg">{copy}</p>
      </Container>
    </section>
  );
}
